<form method="dialog">
  <button
    (click)="clearSelectedValues()"
    class="btn btn-circle btn-ghost btn-sm absolute right-2 top-2"
  >
    ✕
  </button>
</form>

<div class="flex min-h-0 flex-col pt-4">
  <div class="card-title">{{ wizardTitle }}</div>

  @if (loadingProducts && wizardStep === 'product-select') {
    <span class="loading loading-spinner loading-md"></span>
  } @else {
    @switch (wizardStep) {
      @case ('intent') {
        <p class="mb-4 text-sm">What type of license are you adding?</p>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="btn justify-start"
            [class.btn-primary]="licenseIntent === 'primary'"
            [class.btn-outline]="licenseIntent !== 'primary'"
            (click)="selectIntent('primary'); goNext()"
          >
            New primary license (or primary site license)
          </button>
          <button
            type="button"
            class="btn justify-start"
            [class.btn-primary]="licenseIntent === 'addon'"
            [class.btn-outline]="licenseIntent !== 'addon'"
            (click)="selectIntent('addon'); goNext()"
          >
            Add-on to existing license
          </button>
        </div>
      }

      @case ('site-license-choice') {
        <p class="mb-4 text-sm">Will this be a site license?</p>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="btn justify-start"
            [class.btn-primary]="isSiteLicense === true"
            [class.btn-outline]="isSiteLicense === false"
            (click)="selectSiteLicenseChoice(true); goNext()"
          >
            Yes — site license
          </button>
          <button
            type="button"
            class="btn justify-start"
            [class.btn-primary]="isSiteLicense === false"
            [class.btn-outline]="isSiteLicense === true"
            (click)="selectSiteLicenseChoice(false); goNext()"
          >
            No — primary license
          </button>
        </div>
      }

      @case ('parent-select') {
        <p class="mb-2 text-sm">
          @if (parentSelectMode === 'site') {
            Select the primary license this site license will be added under.
          } @else {
            Select the license this add-on will be attached to.
          }
        </p>

        <label class="label cursor-pointer justify-start gap-2 py-2">
          <input
            type="checkbox"
            class="checkbox checkbox-sm"
            [(ngModel)]="showAllParentLicenses"
          />
          <span class="label-text">Show all licenses</span>
        </label>

        <div class="dropdown w-full">
          <div
            tabindex="0"
            role="button"
            class="btn w-full justify-between border-base-content/20 bg-base-100"
            (click)="focusParentLicenseSearchInput()"
          >
            @if (selectedParentLicense) {
              {{ formatLicenseLabel(selectedParentLicense) }}
            } @else {
              <span class="text-base-content/60">Select License</span>
            }
            <app-chevron-down-icon class="h-5 w-5"></app-chevron-down-icon>
          </div>
          <ul
            tabindex="0"
            class="menu dropdown-content z-[1] max-h-96 w-full flex-nowrap overflow-y-auto rounded-box border border-base-content/20 bg-base-100 p-2 shadow"
          >
            <li>
              <input
                #parentLicenseSearchInput
                type="text"
                class="input input-bordered w-full"
                placeholder="Search licenses..."
                [(ngModel)]="parentLicenseSearchText"
              />
            </li>
            @for (
              license of displayParentLicenses;
              track license.id;
              let i = $index
            ) {
              <li
                [ngClass]="{
                  'border-t border-base-content/20': i > 0,
                }"
                (click)="selectParentLicense(license)"
              >
                <div>
                  <p>{{ formatLicenseLabel(license) }}</p>
                  <p class="text-xs opacity-70">
                    Exp:
                    {{ license.expiration | date: 'MM/dd/yyyy' : 'UTC' }}
                  </p>
                </div>
              </li>
            } @empty {
              <li class="disabled">
                <span>No licenses match</span>
              </li>
            }
          </ul>
        </div>
      }

      @case ('product-select') {
        <div class="mb-2 flex flex-row">
          <div class="w-full">
            <p class="px-1 py-2 text-sm">Product</p>
            <div class="dropdown w-full">
              <div
                tabindex="0"
                role="button"
                class="btn w-full justify-between border-base-content/20 bg-base-100"
                (click)="focusProductSearchInput()"
              >
                @if (currentProduct) {
                  {{ currentProduct.code }} - {{ currentProduct.title }} ({{
                    currentProduct.variance_code
                  }})
                } @else {
                  <span class="text-base-content/60">Select Product</span>
                }
                <app-chevron-down-icon class="h-5 w-5"></app-chevron-down-icon>
              </div>
              <ul
                tabindex="0"
                class="menu dropdown-content z-[1] max-h-96 w-full flex-nowrap overflow-y-auto rounded-box border border-base-content/20 bg-base-100 p-2 shadow"
              >
                <li>
                  <input
                    #productSearchInput
                    type="text"
                    class="input input-bordered w-full"
                    placeholder="Search Products..."
                    [(ngModel)]="productFilters.searchText"
                  />
                </li>
                @for (
                  product of displayProducts;
                  track product.id;
                  let i = $index
                ) {
                  <li
                    [ngClass]="{
                      'border-t border-base-content/20': i > 0,
                    }"
                    (click)="handleProductChange(product)"
                  >
                    <p class="flex">
                      <span>{{ product.code }}</span>
                      <span>-</span>
                      <span>
                        {{ product.title }} ({{ product.variance_code }})
                      </span>
                    </p>
                  </li>
                }
              </ul>
            </div>
          </div>
        </div>

        <div class="flex flex-row space-x-4">
          <label class="form-control">
            <div class="label">
              <span class="label-text">Duration (Months)</span>
            </div>
            <select
              class="select select-bordered w-32"
              [(ngModel)]="productFilters.duration"
            >
              <option selected value=""></option>
              @for (duration of variantDurations; track duration) {
                <option [value]="duration">{{ duration }}</option>
              }
            </select>
          </label>
          <label class="form-control">
            <div class="label">
              <span class="label-text">Price Lock Level</span>
            </div>
            <select
              class="select select-bordered w-32"
              [(ngModel)]="productFilters.priceLockLevel"
            >
              <option selected value=""></option>
              @for (priceLockLevel of priceLockLevels; track priceLockLevel) {
                <option [value]="priceLockLevel">{{ priceLockLevel }}</option>
              }
            </select>
          </label>
          <label class="form-control">
            <div class="label">
              <span class="label-text">Delivery Method</span>
            </div>
            <select
              class="select select-bordered w-32"
              [(ngModel)]="productFilters.deliveryMethod"
            >
              <option selected value=""></option>
              <option value="XXX">Physical</option>
              <option value="DNL">Download</option>
            </select>
          </label>
          <label class="form-control">
            <div class="label mt-10 cursor-pointer">
              <input
                type="checkbox"
                class="checkbox mr-2"
                [(ngModel)]="productFilters.showInactiveProducts"
              />
              <span class="label-text">Show Inactive Products</span>
            </div>
          </label>
        </div>

        @if (displayVariants.length > 0) {
          <table class="table table-zebra mt-4">
            <thead class="text-sm font-medium">
              <tr>
                <td>Duration</td>
                <td>Delivery<br />Method</td>
                <td>Lock<br />Level</td>
                <td>Price</td>
                @if (!selectingAddonProducts) {
                  <td>Site<br />Price</td>
                }
                <td></td>
              </tr>
            </thead>
            <tbody>
              @for (variant of displayVariants; track variant.id) {
                <tr>
                  <td>{{ variant.duration_months }}</td>
                  <td>{{ displayDeliveryMethod(variant.delivery_method) }}</td>
                  <td>{{ variant.price_lock_level }}</td>
                  <td>{{ variant.price | currency }}</td>
                  @if (!selectingAddonProducts) {
                    <td>{{ variant.site_price | currency }}</td>
                  }
                  <td>
                    <button
                      type="button"
                      class="btn btn-primary btn-sm"
                      (click)="handleVariantClick(variant)"
                    >
                      Select
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }

        @if (additionalAddons.length > 0) {
          <div class="mt-4 rounded-lg border border-base-content/20 p-3">
            <p class="mb-2 text-sm font-medium">Queued add-ons</p>
            <ul class="list-inside list-disc text-sm">
              @for (addon of additionalAddons; track $index) {
                <li>
                  {{ addon.product.code }} — {{ addon.variant.duration_months }}
                  mo
                </li>
              }
            </ul>
          </div>
        }
      }

      @case ('license-config') {
        <h3 class="pb-2 text-lg font-medium">
          {{ currentProduct?.code }} - {{ currentProduct?.title }} ({{
            currentProduct?.variance_code
          }})
        </h3>

        @if (selectedParentLicense && licenseIntent === 'addon') {
          <p class="mb-2 text-sm italic">
            Dates inherited from parent:
            {{ formatLicenseLabel(selectedParentLicense) }}
          </p>
        }

        @if (isSiteLicense && selectedParentLicense) {
          <p class="mb-2 text-sm italic">
            Site license under:
            {{ formatLicenseLabel(selectedParentLicense) }}
          </p>
        }

        <label class="form-control w-full max-w-xs py-2">
          <div class="label justify-start">
            <input
              [(ngModel)]="isNewLicense"
              type="checkbox"
              class="checkbox"
            />
            <span class="label-text ml-2">New License</span>
          </div>
        </label>
        <label class="form-control w-full max-w-xs py-2">
          <div class="label justify-start">
            <input
              [(ngModel)]="isSiteLicense"
              type="checkbox"
              class="checkbox"
            />
            <span class="label-text ml-2">Site License</span>
          </div>
        </label>

        <div class="flex py-2">
          <label class="form-control w-full max-w-xs">
            <div class="label">
              <span class="label-text">Start Date</span>
            </div>
            <input
              [(ngModel)]="startDate"
              type="date"
              class="input input-bordered w-full max-w-xs"
              (input)="handleStartDateChange()"
            />
          </label>

          <label class="form-control ml-4 w-full max-w-xs">
            <div class="label justify-start">
              <span class="label-text">Expiration Date</span>
              <app-info-circle-icon
                class="tooltip tooltip-top ml-2 h-4 w-4"
                data-tip="For new licenses, the expiration date shown is what the license's expiration date will be once the customer has paid for the license."
              ></app-info-circle-icon>
            </div>
            @if (errorGettingExpirationDate) {
              <p class="ml-4 text-sm text-error">
                {{ errorGettingExpirationDate }}
              </p>
            } @else if (calculatingExpirationDate) {
              <span class="loading loading-spinner loading-md"></span>
            } @else {
              <input
                [(ngModel)]="expirationDate"
                type="date"
                class="input input-bordered w-full max-w-xs"
              />
            }
          </label>
        </div>
      }

      @case ('more-addons') {
        @if (moreAddonsAttachmentTargetLabel && licenseIntent === 'addon') {
          <div
            class="mb-4 rounded-lg border border-base-content/20 bg-base-200/30 p-3"
          >
            <p class="mb-1 text-sm font-medium">
              Additional add-ons will attach to this parent license:
            </p>
            <p class="text-sm">{{ moreAddonsAttachmentTargetLabel }}</p>
          </div>
        }

        @if (queuedLicenses.length > 0) {
          <div class="mb-4 rounded-lg border border-base-content/20 p-3">
            <p class="mb-2 text-sm font-medium">Licenses to add</p>
            <ul class="space-y-2 text-sm">
              @for (item of queuedLicenses; track $index) {
                <li>
                  {{ item.product.code }} - {{ item.product.title }} ({{
                    item.variant.duration_months
                  }}
                  mo)
                </li>
              }
            </ul>
          </div>
        }

        <p class="mb-4 text-sm">
          @if (licenseIntent === 'primary') {
            Add more add-on licenses to this new primary license?
          } @else {
            Add more add-on licenses to this parent license?
          }
        </p>
        <div class="flex flex-col gap-2">
          <button
            type="button"
            class="btn btn-outline justify-start"
            [disabled]="addingLicense"
            (click)="chooseMoreAddons(true)"
          >
            Yes, add another add-on
          </button>
          <button
            type="button"
            class="btn btn-outline btn-primary justify-start"
            [disabled]="addingLicense"
            (click)="chooseMoreAddons(false)"
          >
            No, create these licenses
          </button>
        </div>
      }
    }
  }

  <div class="modal-action mt-8">
    @if (showBackButton) {
      <button type="button" class="btn" (click)="goBack()">Back</button>
    }
    @if (showNextButton) {
      <button
        type="button"
        class="btn btn-primary"
        [disabled]="!canGoNext"
        (click)="goNext()"
      >
        Next
      </button>
    }
    @if (errorAddingLicense) {
      <p class="w-full text-error">{{ errorAddingLicense }}</p>
    }
  </div>
</div>
